import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [debug, setDebug] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDebug(null);
    // Normalize: trim whitespace which can sneak in via copy-paste / mobile autocorrect
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    try {
      const res = await api.post("/admin/login", { email: cleanEmail, password: cleanPassword });
      localStorage.setItem("cw_admin_token", res.data.token);
      localStorage.setItem("cw_admin_email", res.data.email);
      toast.success("Sikeres bejelentkezés");
      navigate("/admin/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const networkMsg = err?.message || "Ismeretlen hiba";
      const info = {
        url: `${API}/admin/login`,
        sentEmail: cleanEmail,
        sentPasswordLength: cleanPassword.length,
        httpStatus: status || "—",
        serverDetail: detail || "(üres)",
        rawError: networkMsg,
        isNetworkError: !err?.response,
      };
      setDebug(info);
      toast.error(detail || (info.isNetworkError ? "Hálózati hiba – nincs válasz a szervertől" : `Hiba: ${status} – ${networkMsg}`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="admin-login-page">
      <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-16 py-8">
        <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#BFA588]">
          <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white border border-[#E5E0D8] p-10 md:p-12"
        >
          <p className="eyebrow mb-4">Admin felület</p>
          <h1 className="font-serif-display text-4xl md:text-5xl text-[#1A1A1A] mb-2">Bejelentkezés</h1>
          <p className="text-[#595959] text-sm mb-10">Csak engedélyezett felhasználók számára.</p>

          <div className="space-y-6">
            <div>
              <Label htmlFor="al-email" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">E-mail</Label>
              <Input
                id="al-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                autoComplete="username"
                required
                className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
              />
            </div>
            <div>
              <Label htmlFor="al-pw" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">Jelszó</Label>
              <div className="relative">
                <Input
                  id="al-pw"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password"
                  autoComplete="current-password"
                  required
                  className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  data-testid="toggle-password"
                  className="absolute right-0 bottom-2 text-[#595959] hover:text-[#1A1A1A]"
                  aria-label={showPw ? "Jelszó elrejtése" : "Jelszó megjelenítése"}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <p className="mt-2 text-[11px] text-[#595959]" data-testid="pw-length">
                  Karakterek száma: {password.length}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-primary mt-10 w-full disabled:opacity-40"
          >
            {loading ? "Belépés…" : "Belépés"}
          </button>

          {debug && (
            <div
              data-testid="debug-panel"
              className="mt-6 p-4 bg-[#FAF9F6] border border-[#E5E0D8] text-[11px] leading-relaxed text-[#595959] font-mono"
            >
              <p className="font-sans uppercase tracking-[0.18em] text-[10px] text-[#9E2A2B] mb-2">Diagnosztika</p>
              <div>URL: <span className="text-[#1A1A1A] break-all">{debug.url}</span></div>
              <div>HTTP státusz: <span className="text-[#1A1A1A]">{debug.httpStatus}</span></div>
              <div>Szerver válasz: <span className="text-[#1A1A1A]">{debug.serverDetail}</span></div>
              <div>Küldött e-mail: <span className="text-[#1A1A1A]">{debug.sentEmail}</span></div>
              <div>Jelszó hossza: <span className="text-[#1A1A1A]">{debug.sentPasswordLength} karakter</span></div>
              <div>Hibatípus: <span className="text-[#1A1A1A]">{debug.isNetworkError ? "Hálózati / CORS" : "Szerver hiba"}</span></div>
              <div>Raw: <span className="text-[#1A1A1A]">{debug.rawError}</span></div>
            </div>
          )}
        </form>
      </div>
    </main>
  );
}
